import { takeEvery, select, put, takeLatest, call, spawn } from 'redux-saga/effects';

import {
  viewer as viewerActions,
  tracking as trackingActions,
  ui as uiActions,
} from 'flux/actions';
import {
  viewer as viewerSelectors,
  tags as tagsSelectors,
  flyerGibs as flyerGibsSelectors,
  flyers as flyersSelectors,
  property as propertySelectors,
} from 'flux/selectors';
import { fetchFlyerPages } from 'flux/api/flyerBundles.api';
import errorHandler from 'errorHandler';
import { getFlyerExtrasBase } from 'utils/retailMediaBanners';

export const filterViewerState = ({ currentPages, isLast, flyerId, viewedPages, slide }) => ({
  currentPages,
  isLast,
  flyerId,
  viewedPages,
  slide,
});

export function* onInitialize() {
  yield spawn(updateViewerBaseExtras);
}

export function* onUiSetDeviceWidth() {
  yield spawn(updateViewerBaseExtras);
}

export function* onViewerEvent({ type, payload }) {
  const previousState = yield select(viewerSelectors.selectViewer);
  const nextState = filterViewerState(payload);

  // update viewedPages
  const { viewedPages: prevViewedPages, flyerId: prevFlyerId } = previousState;
  const { currentPages, flyerId } = nextState;
  nextState.viewedPages =
    prevFlyerId === flyerId
      ? [...new Set([...prevViewedPages, ...currentPages])] // using Set to avoid duplicates
      : [...currentPages];

  yield put(viewerActions.update(nextState));
  yield put(
    viewerActions.updated({
      previousState,
      nextState,
      eventType: type,
    }),
  );
}

export function* onViewerEnrichment({ payload: { id } }) {
  const flyerGib = yield select(flyerGibsSelectors.selectFlyerGib, id);

  if (flyerGib) {
    if (flyerGib.type === 'external_link') {
      const tag = yield select(tagsSelectors.selectTag, id);
      window.open(tag.externalUrl);
    }
  }
}

export function* fetchPages() {
  try {
    const currentFlyer = yield select(flyersSelectors.selectCurrentFlyer);

    if (currentFlyer) {
      const response = yield call(fetchFlyerPages, currentFlyer);
      yield put(viewerActions.setCurrentFlyerPages(currentFlyer.id, response));
    }
  } catch (e) {
    errorHandler(e, true);
  }
}

export function* onIndexClick(action) {
  const { flyerId } = action.payload;
  yield put(
    trackingActions.gtag('event', 'Flyer TOC open', {
      event_category: 'Flyer TOC',
      event_label: `${flyerId}`,
    }),
  );
}

export function* onSectionClick(action) {
  const { flyerId, sectionName } = action.payload;
  yield put(
    trackingActions.gtag('event', 'Section clicked', {
      event_category: 'Flyer TOC',
      event_label: `${flyerId} | ${sectionName}`,
    }),
  );
}

export function* updateViewerBaseExtras() {
  const isActiveRetailMedia = yield select(flyersSelectors.selectCurrentFlyerIsActiveRetailMedia);
  if (!isActiveRetailMedia) return;

  const minWidthToShowTwoPages = yield select(propertySelectors.selectMinWidthToShowTwoPages);
  const isMobile = window.innerWidth < minWidthToShowTwoPages;
  const numberOfPages = yield select(flyersSelectors.selectCurrentFlyerNumberOfPages);
  const backgroundImageUrls = yield select(flyersSelectors.selectRetailMediaCentralBackgroundUrls);

  const extrasBase = getFlyerExtrasBase(numberOfPages, isMobile, backgroundImageUrls);
  yield put(viewerActions.setViewerExtrasBase(extrasBase));
}

export default function* () {
  yield takeEvery(viewerActions.types.INITIALIZE, onInitialize);
  yield takeLatest(uiActions.types.SET_DEVICE_WIDTH, onUiSetDeviceWidth);
  yield takeEvery(
    [
      viewerActions.types.INITIALIZE,
      viewerActions.types.WINDOW_RESIZE_CHANGED_PAGES,
      viewerActions.types.BROWSE,
      viewerActions.types.EXIT,
    ],
    onViewerEvent,
  );
  yield takeEvery(viewerActions.types.ENRICHMENT, onViewerEnrichment);
  yield takeEvery(viewerActions.types.INDEX_BUTTON_CLICK, onIndexClick);
  yield takeEvery(viewerActions.types.SECTION_CLICK, onSectionClick);
  yield takeLatest(viewerActions.types.FETCH_FLYER_PAGES, fetchPages);
}
